import { Fragment, type ReactNode } from 'react';
import { analisar, type Bloco, type Trecho } from '../lib/formatacao';

/**
 * O texto de uma mensagem como o WhatsApp mostra (#615): negrito, itálico,
 * riscado, código, listas e citação, com os links clicáveis e cada "@Nome"
 * mencionado em destaque (#569).
 *
 * Quem decide o que é marcação é o `analisar`; aqui só se desenha a árvore.
 * Nenhum pedaço vira HTML: o React escapa cada texto, e o marcador some do
 * texto — o leitor de tela lê "urgente", não "asterisco urgente asterisco".
 *
 * As chaves são o índice: os pedaços não têm identidade própria, e o texto de
 * uma mensagem enviada não muda mais.
 */
export function TextoFormatado({
  texto,
  mencoes,
}: {
  texto: string;
  /** Quem a nota de fato mencionou (vem da API). "@alguém" fora da lista fica comum. */
  mencoes?: readonly string[] | undefined;
}): ReactNode {
  return <>{analisar(texto, mencoes).map(desenharBloco)}</>;
}

function desenharBloco(bloco: Bloco, indice: number): ReactNode {
  switch (bloco.tipo) {
    case 'paragrafo':
      return <Fragment key={indice}>{bloco.trechos.map(desenharTrecho)}</Fragment>;
    case 'lista': {
      const itens = bloco.itens.map((item, i) => (
        // `value` guarda o número que a pessoa escreveu: "3. … 7. …" não vira "1. 2.".
        <li key={i} value={item.numero ?? undefined}>
          {item.trechos.map(desenharTrecho)}
        </li>
      ));
      return bloco.ordenada ? (
        <ol key={indice} className="bolha__lista">
          {itens}
        </ol>
      ) : (
        <ul key={indice} className="bolha__lista">
          {itens}
        </ul>
      );
    }
    case 'citacao':
      return (
        <blockquote key={indice} className="bolha__citacao">
          {bloco.trechos.map(desenharTrecho)}
        </blockquote>
      );
    case 'bloco-de-codigo':
      return (
        <pre key={indice} className="bolha__bloco-de-codigo">
          <code>{bloco.texto}</code>
        </pre>
      );
  }
}

function desenharTrecho(trecho: Trecho, indice: number): ReactNode {
  switch (trecho.tipo) {
    case 'texto':
      return <Fragment key={indice}>{trecho.texto}</Fragment>;
    case 'link':
      return (
        <a
          key={indice}
          className="bolha__link"
          href={trecho.href}
          target="_blank"
          // `noreferrer` também implica `noopener` — a página de destino não
          // recebe uma referência à nossa aba nem de onde o clique veio.
          rel="noreferrer nofollow"
        >
          {trecho.url}
        </a>
      );
    case 'mencao':
      // Destacada, sem virar link: quem foi chamado já recebeu o aviso.
      return (
        <strong key={indice} className="bolha__mencao">
          {trecho.texto}
        </strong>
      );
    case 'codigo':
      return (
        <code key={indice} className="bolha__codigo">
          {trecho.texto}
        </code>
      );
    case 'negrito':
      return <strong key={indice}>{trecho.filhos.map(desenharTrecho)}</strong>;
    case 'italico':
      return <em key={indice}>{trecho.filhos.map(desenharTrecho)}</em>;
    case 'riscado':
      return <s key={indice}>{trecho.filhos.map(desenharTrecho)}</s>;
  }
}
